import type { Editor } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import { afterCurrentBlock, takeSlashInsertRange } from "@/lib/tiptap/extensions/slashTrigger";
import { paperOutline, sectionLabel, type PaperBlock } from "./paperOutline";

/**
 * Where a table goes in the open paper, and how the sheet says so.
 *
 *   caret    where the `/` was, or after the block holding the caret
 *   section  the end of the caret's section
 *   end      the end of the document
 *
 * ⇥ moves between them (Olivia, tables-modern 1a: "Lands · At your caret").
 */
export type EditorLanding = "caret" | "section" | "end";
export const EDITOR_LANDINGS: EditorLanding[] = ["caret", "section", "end"];

const TYPE: Record<string, string> = { heading: "heading", tableBlock: "table", figureBlock: "figure" };

/** The canvas's top-level blocks, in the shape the outline rules read. */
function blocksOf(doc: PMNode): PaperBlock[] {
  const out: PaperBlock[] = [];
  doc.forEach((n, _pos, i) => {
    out.push({
      id: String(n.attrs.blockId ?? `n${i}`),
      type: TYPE[n.type.name] ?? n.type.name,
      content: n.type.name === "heading"
        ? { text: n.textContent, level: n.attrs.level, numbered: n.attrs.numbered }
        : {},
      sortOrder: i,
    });
  });
  return out;
}

/** The top-level index of the block holding the caret. */
function caretIndex(editor: Editor): number {
  const { $from } = editor.state.selection;
  return $from.depth >= 1 ? $from.index(0) : Math.max(0, editor.state.doc.childCount - 1);
}

export interface LandingInfo {
  /** "§4 Results, after ¶2" — or "the start of the document". */
  caret: string;
  /** "End of §4 Results", or null when the caret is not in a section. */
  section: string | null;
}

/** What the Lands row says for each place, read from the canvas. */
export function describeLandings(editor: Editor): LandingInfo {
  const { doc } = editor.state;
  const blocks = blocksOf(doc);
  const outline = paperOutline(blocks);
  const at = caretIndex(editor);

  let heading: (typeof outline.headings)[number] | null = null;
  let paras = 0;
  doc.forEach((n, _p, i) => {
    if (i > at) return;
    if (n.type.name === "heading") {
      heading = outline.headings.find((h) => h.id === blocks[i].id) ?? null;
      paras = 0;
    } else if (n.type.name === "paragraph" && n.textContent.trim()) {
      paras += 1;
    }
  });
  const h = heading as (typeof outline.headings)[number] | null;
  const where = h ? sectionLabel(h) : "Before the first section";
  const caret = paras > 0 ? `${where}, after ¶${paras}` : h ? `${where}, under its heading` : where;
  return { caret, section: h ? `End of ${sectionLabel(h)}` : null };
}

/** The document position a table goes to for `landing`. */
export function landingPosition(editor: Editor, landing: EditorLanding, atSlash: boolean): { from: number; to: number } | number {
  // The slash's place is used once either way, so a later insert is not sent there.
  const slash = atSlash ? takeSlashInsertRange(editor) : null;
  const { doc } = editor.state;
  if (landing === "end") return doc.content.size;
  if (landing === "section") {
    const at = caretIndex(editor);
    let level: number | null = null;
    doc.forEach((n, _p, i) => { if (i <= at && n.type.name === "heading") level = Number(n.attrs.level) || 1; });
    let pos = doc.content.size;
    let found = false;
    doc.forEach((n, p, i) => {
      if (found || i <= at) return;
      if (n.type.name === "heading" && (level === null || (Number(n.attrs.level) || 1) <= level)) {
        pos = p;
        found = true;
      }
    });
    return pos;
  }
  return slash ?? afterCurrentBlock(editor);
}
